import type { ProvisionedTask } from '../models/useTasks'

/**
 * The org's compute tasks, scoped by the NODE'S TENANT (app#385, PERMISSIONS.md § 2).
 *
 * A task belongs to the org of its node, `nodes.owner_org_id`. Only a task with no node yet (the
 * container has not registered) belongs to the org of its network. This is the router's
 * `taskBillingOrg` (`coalesce(nodes.owner_org_id, networks.org_id)`) and the `tasks` SELECT policy
 * of router migration 0228.
 *
 * ⚠⚠ NOT `networks.org_id` FOR EVERY TASK. The two orgs part for a node in another org's network.
 * Scoped by the network, the table showed the task under the network's org, where `task_stop` and
 * `deprovision_node` refuse it (router#1779), and hid it from the node's org, which pays for it.
 *
 * ⚠ TWO READS, NOT ONE. The rule has two arms, each on a different embedded table, and PostgREST
 * cannot put an OR across two embeds in one filter. `useTasks.get` reads each arm with an `!inner`
 * embed, and this module merges them.
 *
 * ⚠ This module imports types only, so a plain `node --experimental-strip-types` test reaches it.
 */

/** A `tasks` row as `useTasks` reads it, after `camelcaseKeys`. */
export type RawTask = {
  id: string
  nodeId: string | null
  nodeName: string
  networkId: string
  cpu: number | null
  memoryMb: number | null
  createdAt: string
  completedAt: string | null
  deadlineAt: string | null
  stopReason: string | null
  /** `tasks.owner_id`: the user who provisioned the task. */
  ownerId: string | null
  /**
   * NULL when the network is in an org this reader is not a member of (networks RLS). ⚠ The
   * networks SELECT policy does not test `deleted_at`, so a removed network comes back here.
   */
  networks: { name: string; orgId: string; deletedAt: string | null } | null
  /** NULL for a task with no node, and for a node this reader cannot read (nodes RLS). */
  nodes: { name: string; deletedAt: string | null; ownerOrgId: string; ownerUserId: string | null } | null
}

/** The org a task belongs to, or null when this reader cannot tell. */
export const rawTaskTenant = (row: RawTask): string | null => {
  if (row.nodeId !== null) return row.nodes?.ownerOrgId ?? null
  return row.networks?.orgId ?? null
}

export const toProvisionedTask = (row: RawTask, orgId: string): ProvisionedTask => ({
  id: row.id,
  nodeId: row.nodeId,
  nodeName: row.nodes?.name ?? row.nodeName,
  nodeReadable: row.nodes != null,
  nodeDeleted: row.nodes?.deletedAt != null,
  nodeOwnerOrgId: row.nodes?.ownerOrgId ?? null,
  networkId: row.networkId,
  networkName: row.networks?.name ?? '',
  // ⚠ A removed network is not loaded either (`useNetwork` reads live networks only). Since
  // router migration 0228, the task of a node whose network is removed comes back on the node arm.
  networkInOrg: row.networks != null && row.networks.orgId === orgId && row.networks.deletedAt == null,
  cpu: row.cpu,
  memoryMb: row.memoryMb,
  createdAt: row.createdAt,
  completedAt: row.completedAt,
  deadlineAt: row.deadlineAt,
  stopReason: row.stopReason ?? null,
  // ⚠ The router's rule (`computeGate`): the NODE's owner gates its compute, and only a task
  // with no node yet falls back to the user who provisioned it.
  controlOwnerId: row.nodeId ? (row.nodes?.ownerUserId ?? null) : row.ownerId,
})

/**
 * The rows of both reads whose tenant is `orgId`, once each, newest first.
 *
 * ⚠ The tenant test is applied AGAIN here, not only in the two queries. It is the rule, and a
 * row that does not pass it must not get a Stop or Terminate button under this org: those tools
 * ask the node's tenant, and they would refuse it (or act on another org's machine for a reader
 * who is a member of both).
 */
export const mergeTenantTasks = (nodeArm: RawTask[], noNodeArm: RawTask[], orgId: string): ProvisionedTask[] => {
  const byId = new Map<string, ProvisionedTask>()
  for (const row of [...nodeArm, ...noNodeArm]) {
    if (rawTaskTenant(row) !== orgId) continue
    byId.set(row.id, toProvisionedTask(row, orgId))
  }
  // Same order as the single read before app#385: newest first. The id breaks a tie, so two
  // reads of the same rows always give the same order.
  return [...byId.values()].sort(
    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0),
  )
}
